import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { useDispatch } from "react-redux";
import {
  setPurchaseOrderList,
  setPurchaseOrderDetail
} from "../../store/slices/purchaseOrderSlice";
import {
  getPurchaseOrderListQuery,
  getPurchaseOrderDetailQuery,
  createPurchaseOrderMutation,
  updatePurchaseOrderStatusMutation as updatePurchaseOrderMutation
} from "../../Graphql/querries/purchaseOrderQuery";
import {
  GetPurchaseOrderListRequest,
  GetPurchaseOrderListResponse,
  GetPurchaseOrderDetailRequest,
  GetPurchaseOrderDetailResponse,
  CreatePurchaseOrderRequest,
  CreatePurchaseOrderResponse,
  UpdatePurchaseOrderRequest,
  UpdatePurchaseOrderResponse
} from "../../OrderMFE/Orders/types/purchaseOrder";
import { store } from "../../store/store";

/**
 * useGetPurchaseOrderList
 * Fetch purchase order list
 */
export const useGetPurchaseOrderList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    GetPurchaseOrderListResponse,
    Error,
    GetPurchaseOrderListRequest
  >({
    mutationKey: ["getPurchaseOrderList"],
    mutationFn: (input) =>
      fetchGraphQL<
        GetPurchaseOrderListResponse,
        { input: GetPurchaseOrderListRequest }
      >(getPurchaseOrderListQuery, { input }, setAlert),
    onSuccess: (response) => {
      dispatch(
        setPurchaseOrderList(response.getPurchaseOrderList.purchaseOrderList)
      );
    }
  });
};

/**
 * useGetPurchaseOrderDetail
 * Get purchase order details by id
 */
export const useGetPurchaseOrderDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    GetPurchaseOrderDetailResponse,
    Error,
    GetPurchaseOrderDetailRequest
  >({
    mutationKey: ["getPurchaseOrderDetail"],
    mutationFn: async (input) => {
      // Mock implementation: Find order from store first
      // TODO: Replace with actual API call when backend is available
      const state = store.getState();
      const foundOrder = state.purchaseOrders.purchaseOrderList.find(
        (po) => po.id === input.orderId
      );
      if (foundOrder) {
        return {
          getPurchaseOrderDetail: {
            code: "SUCCESS",
            purchaseOrder: foundOrder
          }
        } as GetPurchaseOrderDetailResponse;
      }
      // If not found in store, try API call
      return fetchGraphQL<
        GetPurchaseOrderDetailResponse,
        { input: GetPurchaseOrderDetailRequest }
      >(getPurchaseOrderDetailQuery, { input }, setAlert);
    },
    onSuccess: (response) => {
      if (response.getPurchaseOrderDetail.purchaseOrder) {
        dispatch(
          setPurchaseOrderDetail(response.getPurchaseOrderDetail.purchaseOrder)
        );
      }
    }
  });
};

/**
 * useCreatePurchaseOrder
 * Create a new purchase order
 */
export const useCreatePurchaseOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    CreatePurchaseOrderResponse,
    Error,
    CreatePurchaseOrderRequest
  >({
    mutationKey: ["createPurchaseOrder"],
    mutationFn: (input) =>
      fetchGraphQL<CreatePurchaseOrderResponse, CreatePurchaseOrderRequest>(
        createPurchaseOrderMutation,
        input,
        setAlert
      )
  });
};

/**
 * useUpdatePurchaseOrder
 * Update existing purchase order information
 */
export const useUpdatePurchaseOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    UpdatePurchaseOrderResponse,
    Error,
    UpdatePurchaseOrderRequest
  >({
    mutationKey: ["updatePurchaseOrder"],
    mutationFn: (input) =>
      fetchGraphQL<UpdatePurchaseOrderResponse, UpdatePurchaseOrderRequest>(
        updatePurchaseOrderMutation,
        input,
        setAlert
      ),
    onSuccess: (response) => {
      dispatch(
        setPurchaseOrderDetail(response.updatePurchaseOrder.purchaseOrder)
      );
    }
  });
};
